import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { getPlaylistCoverUrl, optimizeImage } from "@/lib/mediaUrls";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";

// Music icon for empty playlists
const MusicIcon = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
  </svg>
);

// Plus icon
const PlusIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

// Lock icon for private playlists
const LockIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

// Playlist Card Component
const PlaylistCard = ({ playlist, showCreator = false }) => {
  const trackCount = playlist.track_ids?.length || 0;
  const coverUrl = playlist.cover_image ? getPlaylistCoverUrl(playlist) : null;
  
  return (
    <Link to={`/playlist/${playlist.playlist_id}`} data-testid={`playlist-card-${playlist.playlist_id}`}>
      <Card className="bg-[#1a1a1e] border-purple-900/30 hover:border-purple-500/50 transition-all duration-300 overflow-hidden group cursor-pointer hover:transform hover:scale-[1.02]">
        {/* Cover Image */}
        <div className="aspect-square relative bg-gradient-to-br from-purple-900/50 to-pink-900/50 overflow-hidden">
          {coverUrl ? (
            <img
              src={optimizeImage(coverUrl, 'card')}
              alt={playlist.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <MusicIcon className="w-16 h-16 text-purple-400/50" />
            </div>
          )}
          
          {/* Play overlay */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-purple-600/90 flex items-center justify-center">
              <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
          
          {/* Private badge */}
          {!playlist.is_public && (
            <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5">
              <LockIcon className="w-3.5 h-3.5 text-gray-300" />
            </div>
          )}
        </div>
        
        {/* Info */}
        <div className="p-4">
          <h3 className="font-bold text-white truncate group-hover:text-purple-300 transition-colors">
            {playlist.name}
          </h3>
          
          {showCreator && playlist.creator && (
            <p className="text-sm text-gray-400 truncate mt-1">
              by {playlist.creator.artist_name || playlist.creator.name}
            </p>
          )}
          
          <p className="text-sm text-gray-500 mt-1">
            {trackCount} {trackCount === 1 ? 'track' : 'tracks'}
          </p>
        </div>
      </Card>
    </Link>
  );
};

// Create Playlist Modal
const CreatePlaylistModal = ({ isOpen, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter a playlist name");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await api.post('/playlists', {
        name: name.trim(),
        description: description.trim() || null,
        is_public: isPublic
      });
      
      toast.success("Playlist created!");
      onCreated(response.data);
      setName("");
      setDescription("");
      setIsPublic(true);
      onClose();
    } catch (error) {
      toast.error("Failed to create playlist");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card 
        className="bg-[#1a1a1e] border-purple-900/30 w-full max-w-md p-6"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold text-white mb-6">Create Playlist</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Playlist Name *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Awesome Playlist"
              className="bg-black/50 border-purple-900/50 text-white"
              maxLength={50}
              data-testid="playlist-name-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description (optional)
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this playlist about?"
              className="bg-black/50 border-purple-900/50 text-white resize-none"
              rows={3}
              maxLength={200}
              data-testid="playlist-description-input"
            />
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is-public"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="w-4 h-4 rounded border-purple-500 bg-black/50 text-purple-600 focus:ring-purple-500"
              data-testid="playlist-public-checkbox"
            />
            <label htmlFor="is-public" className="text-sm text-gray-300">
              Make this playlist public
            </label>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold"
              data-testid="create-playlist-submit"
            >
              {isSubmitting ? "Creating..." : "Create Playlist"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

const PlaylistsPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [myPlaylists, setMyPlaylists] = useState([]);
  const [publicPlaylists, setPublicPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState("my"); // "my" or "discover"
  
  useEffect(() => {
    fetchCurrentUser();
    fetchPlaylists();
  }, []);
  
  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      // User not logged in
    }
  };
  
  const fetchPlaylists = async () => {
    setLoading(true);
    try {
      // Fetch both my playlists and public playlists
      const [myRes, publicRes] = await Promise.all([
        api.get('/playlists/my').catch(() => ({ data: { playlists: [] } })),
        api.get('/playlists/public').catch(() => ({ data: { playlists: [] } }))
      ]);
      
      setMyPlaylists(myRes.data.playlists || []);
      setPublicPlaylists(publicRes.data.playlists || []);
    } catch (error) {
      console.error('Failed to fetch playlists:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePlaylistCreated = (data) => {
    // Refetch playlists
    fetchPlaylists();
    // Navigate to the new playlist
    navigate(`/playlist/${data.playlist_id}`);
  };
  
  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="playlists-page">
      <SharedNavigation currentUser={currentUser} activePage="playlists" />
      
      <div className="pt-24 pb-16 px-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-2">Playlists</h1>
            <p className="text-gray-400">Create and discover curated collections</p>
          </div>
          
          {currentUser && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold"
              data-testid="create-playlist-btn"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Playlist
            </Button>
          )}
        </div>
        
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-purple-900/30">
          {currentUser && (
            <button
              onClick={() => setActiveTab("my")}
              className={`pb-4 px-2 font-medium transition-colors ${
                activeTab === "my"
                  ? "text-purple-400 border-b-2 border-purple-500"
                  : "text-gray-400 hover:text-white"
              }`}
              data-testid="my-playlists-tab"
            >
              My Playlists ({myPlaylists.length})
            </button>
          )}
          <button
            onClick={() => setActiveTab("discover")}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === "discover"
                ? "text-purple-400 border-b-2 border-purple-500"
                : "text-gray-400 hover:text-white"
            }`}
            data-testid="discover-playlists-tab"
          >
            Discover
          </button>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <>
            {/* My Playlists Tab */}
            {activeTab === "my" && currentUser && (
              <div>
                {myPlaylists.length === 0 ? (
                  <div className="text-center py-16">
                    <MusicIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">No playlists yet</h3>
                    <p className="text-gray-400 mb-6">Create your first playlist to start curating your favorite tracks</p>
                    <Button
                      onClick={() => setShowCreateModal(true)}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                    >
                      <PlusIcon className="w-5 h-5 mr-2" />
                      Create Your First Playlist
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {myPlaylists.map((playlist) => (
                      <PlaylistCard key={playlist.playlist_id} playlist={playlist} />
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Discover Tab */}
            {(activeTab === "discover" || !currentUser) && (
              <div>
                {publicPlaylists.length === 0 ? (
                  <div className="text-center py-16">
                    <MusicIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">No public playlists yet</h3>
                    <p className="text-gray-400">Be the first to create a public playlist!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {publicPlaylists.map((playlist) => (
                      <PlaylistCard key={playlist.playlist_id} playlist={playlist} showCreator={true} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Create Playlist Modal */}
      <CreatePlaylistModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handlePlaylistCreated}
      />
    </div>
  );
};

export default PlaylistsPage;
